import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { HardHat, ArrowRight } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleSignIn() {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (user) navigate("/dashboard");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (ex) {
      setErr(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-canvas" data-testid="login-page">
      {/* Left: Hero */}
      <div
        className="relative hidden lg:block"
        style={{
          backgroundImage:
            "linear-gradient(180deg, rgba(15,23,42,0.75), rgba(15,23,42,0.92)), url('https://images.unsplash.com/photo-1758609554462-c415542ce531?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwcGFsbGV0JTIwd2FyZWhvdXNlJTIwcmFjayUyMHN0b3JhZ2V8ZW58MHx8fHwxNzc2Nzc1MTk1fDA&ixlib=rb-4.1.0&q=85')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bp-grid opacity-40" />
        <div className="relative h-full p-12 flex flex-col">
          <div className="flex items-center gap-3 bg-white p-3 w-fit">
            <img
              src="/brand/adarsh-logo.png"
              alt="Adarsh Udyog"
              className="h-12 w-auto object-contain"
            />
          </div>
          <div className="mt-auto">
            <div className="label-tech text-white/70 mb-4">Est. Industrial Storage Systems</div>
            <h2 className="display text-white text-5xl lg:text-6xl font-black tracking-tighter uppercase leading-[0.9]">
              Engineer<br />pallet racks<br />with precision.
            </h2>
            <p className="text-white/70 mt-6 max-w-md leading-relaxed">
              Configure uprights, beams & panels against verified master data.
              Generate professional quotations in seconds.
            </p>
            <div className="mt-10 grid grid-cols-3 gap-px bg-white/10 border border-white/10">
              {[
                ["01", "Master data"],
                ["02", "Live calc"],
                ["03", "PDF quote"],
              ].map(([n, l]) => (
                <div key={n} className="p-4 bg-[#0F172A]">
                  <div className="mono text-brand text-2xl font-bold">{n}</div>
                  <div className="label-tech text-white/70 mt-1">{l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right: Form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md panel p-8" data-testid="login-card">
          <div className="label-tech">Secure portal — 01</div>
          <h1 className="display text-4xl font-black tracking-tighter uppercase text-ink mt-2">
            Sign in
          </h1>
          <p className="text-ink-muted mt-2 mb-8">
            Access the pallet rack configurator workspace.
          </p>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="label-tech mb-2 block">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-tech"
                data-testid="login-email"
                autoComplete="email"
              />
            </div>
            <div>
              <label className="label-tech mb-2 block">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-tech"
                data-testid="login-password"
                autoComplete="current-password"
              />
            </div>

            {err && (
              <div
                className="border border-brand-danger bg-red-50 text-brand-danger px-3 py-2 text-sm"
                data-testid="login-error"
              >
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
              data-testid="login-submit"
            >
              {loading ? "Authenticating…" : "Enter workspace"}
              <ArrowRight size={16} />
            </button>
          </form>

          <div className="flex items-center gap-3 my-6">
            <div className="h-px bg-line flex-1" />
            <span className="label-tech">Or</span>
            <div className="h-px bg-line flex-1" />
          </div>

          <button
            type="button"
            onClick={startGoogleSignIn}
            className="btn-secondary w-full"
            data-testid="google-signin-btn"
          >
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.7-6.7C35.7 2.4 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.9 6.1C12.4 13.6 17.7 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.9 7.2l7.6 5.9c4.4-4.1 7.1-10.1 7.1-17.6z"/>
              <path fill="#FBBC05" d="M10.5 28.7c-.5-1.4-.8-2.9-.8-4.7s.3-3.3.8-4.7l-7.9-6.1C.9 16.6 0 20.2 0 24s.9 7.4 2.6 10.8l7.9-6.1z"/>
              <path fill="#34A853" d="M24 48c6.2 0 11.5-2 15.3-5.6l-7.6-5.9c-2.1 1.4-4.8 2.2-7.7 2.2-6.3 0-11.6-4.1-13.5-9.8l-7.9 6.1C6.5 42.6 14.6 48 24 48z"/>
            </svg>
            Continue with Google
          </button>
          <div className="text-[0.7rem] text-ink-muted mt-3 text-center">
            Only pre-approved Google accounts can sign in.
          </div>
        </div>
      </div>
    </div>
  );
}
